import { FEATURE_FLAGS, parseCostApiKeys } from "@repo/shared";
import type { CostOverrideMode, LocalizedString } from "@repo/shared/types";
import { CostTypeCategory } from "@repo/shared/types";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogBody,
  DialogCancel,
  DialogDescription,
  DialogScrollContent,
  DialogScrollFooter,
  DialogScrollHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { LocalizedInput } from "@/components/ui/localized-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { COST_API_KEYS } from "@/lib/constants/cost-api-keys";
import { useFeatureFlag } from "@/lib/hooks/use-feature-flags";
import { useCreateCostType, useUpdateCostType } from "@/lib/hooks/use-portfolio-collections";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { AdvisorOverrideSelect } from "./advisor-override-select";
import { T } from "./cost-type-dialog.i18n";

type CostTypeDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  portfolioCollectionId: string;
  costType: any | null;
};

export function CostTypeDialog({
  open,
  onOpenChange,
  portfolioCollectionId,
  costType,
}: CostTypeDialogProps) {
  const { userLanguage, tenantSupportedLanguages } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);

  const createCostType = useCreateCostType();
  const updateCostType = useUpdateCostType();
  const isPending = createCostType.isPending || updateCostType.isPending;

  const CATEGORY_LABELS: Record<CostTypeCategory, string> = {
    RECURRING: t.categoryRecurring,
    DEPOSIT: t.categoryDeposit,
    WITHDRAWAL: t.categoryWithdrawal,
  };
  const [name, setName] = useState("");
  const [nameI18n, setNameI18n] = useState<LocalizedString>({});
  const [category, setCategory] = useState<CostTypeCategory>(CostTypeCategory.RECURRING);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  // QL-20 — shown and sent only while the tenant's cost_overrides feature is on.
  const { enabled: overridesEnabled } = useFeatureFlag(FEATURE_FLAGS.COST_OVERRIDES);
  const [advisorOverride, setAdvisorOverride] = useState<CostOverrideMode>("off");

  useEffect(() => {
    if (open) setAdvisorOverride(costType?.advisorOverride ?? "off");
  }, [open, costType]);

  const isEditing = costType !== null;
  const editingCostTypeName = costType ? localize(costType.nameI18n) : "";

  useEffect(() => {
    if (open) {
      if (costType) {
        setName(localize(costType.nameI18n));
        setNameI18n(costType.nameI18n || {});
        setCategory(costType.category);
        setSelectedKeys(parseCostApiKeys(costType.apiKeys));
      } else {
        setName("");
        setNameI18n({});
        setCategory(CostTypeCategory.RECURRING);
        setSelectedKeys([]);
      }
      setError(null);
    }
  }, [open, costType, localize]);

  const handleKeyToggle = (key: string, checked: boolean) => {
    if (checked) {
      setSelectedKeys((prev) => [...prev, key]);
    } else {
      setSelectedKeys((prev) => prev.filter((k) => k !== key));
    }
  };

  // Click on priority badge: move up by 1, do nothing at position 1
  const handlePriorityClick = (key: string) => {
    const index = selectedKeys.indexOf(key);
    if (index > 0) {
      // Move up by 1
      const newKeys = [...selectedKeys];
      [newKeys[index], newKeys[index - 1]] = [newKeys[index - 1], newKeys[index]];
      setSelectedKeys(newKeys);
    }
    // At position 1 (index 0), do nothing
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError(t.nameRequired);
      return;
    }

    if (selectedKeys.length === 0) {
      setError(t.selectAtLeastOneKey);
      return;
    }

    // Build the final nameI18n with the primary language value
    const finalNameI18n: LocalizedString = {
      ...nameI18n,
      [userLanguage]: name.trim(),
    };

    const submitData = {
      nameI18n: finalNameI18n,
      category,
      apiKeys: selectedKeys,
      ...(overridesEnabled && { advisorOverride }),
    };

    const callbacks = {
      onSuccess: () => onOpenChange(false),
      onError: (err: Error) => setError(err.message || t.failedToSave),
    };

    if (isEditing) {
      updateCostType.mutate(
        { collectionId: portfolioCollectionId, ctId: costType.id, data: submitData },
        callbacks,
      );
    } else {
      createCostType.mutate({ collectionId: portfolioCollectionId, data: submitData }, callbacks);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogScrollContent className="max-w-2xl">
        <DialogScrollHeader>
          <DialogTitle>
            {isEditing
              ? editingCostTypeName
                ? `${t.editCostType} (${editingCostTypeName})`
                : t.editCostType
              : t.addCostType}
          </DialogTitle>
          <DialogDescription>
            {isEditing ? t.editDescription : t.createDescription}
          </DialogDescription>
        </DialogScrollHeader>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col min-h-0">
          <DialogBody className="space-y-6">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}

            <LocalizedInput
              id="name"
              label={t.name}
              value={name}
              i18nValue={nameI18n}
              onChange={setName}
              onI18nChange={setNameI18n}
              supportedLanguages={tenantSupportedLanguages}
              placeholder={t.namePlaceholder}
              required
              disabled={isPending}
            />

            <div className="space-y-2">
              <Label htmlFor="category">{t.category}</Label>
              <Select
                value={category}
                onValueChange={(value) => setCategory(value as CostTypeCategory)}
                disabled={isPending}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{t.categoryDescription}</p>
            </div>

            <div className="space-y-4">
              <Label>
                {t.apiKeyMappings} <span className="text-destructive">*</span>
              </Label>
              <p className="text-sm text-muted-foreground">{t.apiKeyDescription}</p>

              {/* Available keys grouped by source */}
              <div className="space-y-4">
                {Object.entries(COST_API_KEYS).map(([groupKey, group]) => (
                  <div key={groupKey} className="space-y-2">
                    <Label className="text-sm font-medium text-muted-foreground">
                      {group.label}
                    </Label>
                    <div className="grid grid-cols-2 gap-2">
                      {group.keys.map((apiKey) => {
                        const isSelected = selectedKeys.includes(apiKey.key);
                        const priority = selectedKeys.indexOf(apiKey.key) + 1;
                        return (
                          <div key={apiKey.key} className="flex items-center space-x-2">
                            <Checkbox
                              id={apiKey.key}
                              checked={isSelected}
                              onCheckedChange={(checked) =>
                                handleKeyToggle(apiKey.key, checked === true)
                              }
                              disabled={isPending}
                            />
                            <Label
                              htmlFor={apiKey.key}
                              className="text-sm font-normal cursor-pointer"
                            >
                              {apiKey.label}
                            </Label>
                            {isSelected && (
                              <button
                                type="button"
                                onClick={() => handlePriorityClick(apiKey.key)}
                                disabled={isPending}
                                className="h-5 w-5 rounded-full bg-primary text-primary-foreground text-xs font-medium flex items-center justify-center hover:bg-primary/80 transition-colors"
                              >
                                {priority}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {overridesEnabled && (
              <AdvisorOverrideSelect
                id="cost-type-advisor-override"
                value={advisorOverride}
                onChange={setAdvisorOverride}
                disabled={isPending}
              />
            )}
          </DialogBody>

          <DialogScrollFooter>
            <DialogCancel disabled={isPending}>{t.cancel}</DialogCancel>
            <Button type="submit" loading={isPending}>
              {isEditing ? t.save : t.addCostType}
            </Button>
          </DialogScrollFooter>
        </form>
      </DialogScrollContent>
    </Dialog>
  );
}
